import {AfterViewInit, Component, inject, Input, OnDestroy, OnInit} from "@angular/core";
import {AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators} from "@angular/forms";
import {Store} from "@ngrx/store";
import {AssetValuationTypeEnum} from "../../../models/asset-valuation/asset-valuation-type.enum";
import {AssetValuation} from "../../../models/asset-valuation/asset-valuation";
import {AssetValuationScenario} from "../../../models/asset-valuation/asset-valuation-scenario";
import {Code, CodeTableEnum} from "../../../../shared/model/code";
import {selectCodesFromTable} from "../../../../shared/store/shared.selectors";
import {dateAfterValidator, noWhitespaceValidator, numberNotBiggerThan190000m} from "../../../../shared/utils/form-validators";
import {CashflowTypeEnum, isCashflowTypeEnum} from "../../../models/asset-valuation/cashflow-type.enum";
import {DateUtil} from "../../../../shared/utils/date-util";
import {ValuationInput} from "../../../models/asset-valuation/discount-multiple-valuation";
import {AssetCashflow, EMPTY_ASSET_CASHFLOW} from "../../../models/asset-valuation/asset-cashflow";
import {DecimalFormatPipe} from "../../../../shared/pipes/decimal-format/decimal-format.pipe";
import {AssetValuationDetailActions} from "../../../store/asset.actions";
import {DiscountMultipleValuation} from "../../../generated-sources/api";
import {MatDialogRef} from "@angular/material/dialog";
import {map, Subscription, switchMap} from "rxjs";
import {DateTime} from "luxon";
import {TraceableDate} from "../../../../shared/model/traceable";
import {tap} from "rxjs/operators";
import ValuationInputEnum = DiscountMultipleValuation.ValuationInputEnum;
import {selectReportDate} from "../../../store/asset/asset.selectors";

interface RecurringOptions {
    recurringFrequency: string;
    recurringEndCondition: string;
    numberOfOccurrences?: number;
    recurringEndDate?: DateTime;
}

@Component({
    selector: "valumize-asset-cashflow-new-dialog",
    templateUrl: "./asset-cashflow-new-dialog.component.html",
    styleUrls: ["./asset-cashflow-new-dialog.component.scss"],
    standalone: false
})
export class AssetCashflowNewDialogComponent implements OnInit, OnDestroy, AfterViewInit {

    @Input() valuationType?: AssetValuationTypeEnum;
    @Input() assetValuation?: AssetValuation;
    @Input() scenarios?: AssetValuationScenario[];

    private readonly store = inject(Store);
    private readonly formBuilder = inject(FormBuilder);
    private readonly dialogRef = inject(MatDialogRef<AssetCashflowNewDialogComponent>);

    subscriptions: Subscription[] = [];
    hasDuplicates: boolean = false;
    columns = ["select", "type", "low", "base", "high"];
    codeTableCashflowType = CodeTableEnum.SHARED_CASHFLOWTYPE;
    fundReportDate?: TraceableDate;

    selectCodesCashflowType$ = this.store.select(selectCodesFromTable(this.codeTableCashflowType));

    cashflowForm: FormGroup = this.formBuilder.group({
        selectedRowIndex: this.formBuilder.control<number | null>(null),
        cashflowType: this.formBuilder.control<string>("", {nonNullable: true, validators: [Validators.required, noWhitespaceValidator()]}),
        cashflowDate: this.formBuilder.control<Date | null>(null, {nonNullable: true, validators: [Validators.required, noWhitespaceValidator()]}),
        recurringCashflow: this.formBuilder.control<boolean>(false, {nonNullable: true}),
        rows: this.formBuilder.array([]),
    });

    constructor() {
    }

    get rows(): FormArray {
        return this.cashflowForm.get("rows") as FormArray;
    }

    get recurringEndDateControl() {
        return this.cashflowForm.get("recurringEndDate");
    }

    get recurringEndDateError() {
        return this.recurringEndDateControl?.hasError("dateAfter");
    }

    get numberOfOccurrencesError() {
        return this.cashflowForm.get("numberOfOccurrences")?.hasError("min")
            || this.cashflowForm.get("numberOfOccurrences")?.hasError("max");
    }

    ngOnInit(): void {
        this.columns = this.isValuationTypeDmv() ? ["select", "type", "low", "base", "high"] : ["type", "low", "base", "high"];

        this.rows.push(
            this.formBuilder.group({
                type: new FormControl<string>("Amount"),
                low: this.formBuilder.control<number | null>(null, {validators: [numberNotBiggerThan190000m()]}),
                base: this.formBuilder.control<number | null>(null, {validators: [numberNotBiggerThan190000m()]}),
                high: this.formBuilder.control<number | null>(null, {validators: [numberNotBiggerThan190000m()]})
            })
        );

        if (this.isValuationTypeDmv()) {
            this.rows.push(
                this.formBuilder.group({
                    type: new FormControl<string>("NAV Multiple"),
                    low: this.formBuilder.control<number | null>(null, {validators: [numberNotBiggerThan190000m()]}),
                    base: this.formBuilder.control<number | null>(null, {validators: [numberNotBiggerThan190000m()]}),
                    high: this.formBuilder.control<number | null>(null, {validators: [numberNotBiggerThan190000m()]})
                })
            );
        }

        this.cashflowForm.setValidators(this.amountOrNavFormValidator());
        this.onRowSelect(0);

        this.subscriptions.push(
            this.store.select(selectReportDate).pipe(map((fundReportDate) => {
                this.fundReportDate = fundReportDate;
            })).subscribe());
    }

    ngAfterViewInit() {
        const cfTypeSubscr = this.cashflowForm.get("cashflowType")?.valueChanges.subscribe(() => this.duplicateCashflowsFormValidator());
        if (cfTypeSubscr) {
            this.subscriptions.push(cfTypeSubscr);
        }

        const cfDateSubscr = this.cashflowForm.get("cashflowDate")?.valueChanges.subscribe(() => this.duplicateCashflowsFormValidator());
        if (cfDateSubscr) {
            this.subscriptions.push(cfDateSubscr);
        }

        const recurringCfSubscr = this.cashflowForm.get("recurringCashflow")?.valueChanges.pipe(
            tap((isChecked: boolean) => {
                if (isChecked) {
                    this.cashflowForm.addControl("recurringFrequency", this.formBuilder.control<string>("",
                        [Validators.required, noWhitespaceValidator()]));
                    this.cashflowForm.addControl("recurringEndCondition", this.formBuilder.control<string>("exit",
                        [Validators.required, noWhitespaceValidator()]));
                    this.cashflowForm.addControl("numberOfOccurrences", this.formBuilder.control<number | null>(null,
                        [Validators.min(1), Validators.max(99)]));
                    this.cashflowForm.addControl("recurringEndDate", this.formBuilder.control<Date | null>(null,
                        [dateAfterValidator(DateUtil.toJsDate(this.fundReportDate?.date))]));
                } else {
                    this.cashflowForm.removeControl("recurringFrequency");
                    this.cashflowForm.removeControl("recurringEndCondition");
                    this.cashflowForm.removeControl("numberOfOccurrences");
                    this.cashflowForm.removeControl("recurringEndDate");
                }
            }),
            switchMap(() => this.cashflowForm.get("recurringEndCondition")?.valueChanges ?? [])
        ).subscribe((value: string | null) => {
            if (value) {
                this.updateValidatorsBasedOnEndCondition(value);  // Update validators when end condition changes
            }
        });

        if (recurringCfSubscr) {
            this.subscriptions.push(recurringCfSubscr);
        }
    }

    isValuationTypeDmv = () => this.valuationType === AssetValuationTypeEnum.DiscountMultipleValuation;

    isInjectionCashflow = () => this.cashflowForm.controls["cashflowType"].value === CashflowTypeEnum.Injection;

    onRowSelect(index: number): void {
        this.cashflowForm.get("selectedRowIndex")?.setValue(index);
        this.rows.controls.forEach((row, i) => {
            if (i === index) {
                row.enable();
            } else {
                row.disable();
            }
        });
    }

    filterAssetCashflowTypes = (codes: Code[] | null): Code[] => codes?.filter(code => code.code && isCashflowTypeEnum(code.code)) ?? [];

    createCashflows(assetValuation: AssetValuation, scenarios: AssetValuationScenario[]) {
        const selectedRowIndex = this.cashflowForm.controls["selectedRowIndex"].value;
        const cfType = this.cashflowForm.controls["cashflowType"].value;
        const cfDate = DateUtil.toIsoDate(this.cashflowForm.controls["cashflowDate"].value) ?? DateUtil.isoDate();
        const exitDate = DateUtil.fromIsoDate(assetValuation.exitDateAssumption.date);
        const amountRow = this.rows.controls.find(row => row.get("type")?.value === "Amount");
        const navMultipleRow = this.rows.controls.find(row => row.get("type")?.value === "NAV Multiple");

        const isRecurring = this.cashflowForm.controls["recurringCashflow"].value;
        const recurringFrequency = this.cashflowForm.controls["recurringFrequency"]?.value;
        const recurringEndCondition = this.cashflowForm.controls["recurringEndCondition"]?.value;
        const numberOfOccurrences = this.cashflowForm.controls["numberOfOccurrences"]?.value;
        const recurringEndDate = this.cashflowForm.controls["recurringEndDate"]?.value ? DateTime.fromJSDate(this.cashflowForm.controls["recurringEndDate"].value) : undefined;
        const recurringOptions: RecurringOptions | undefined = isRecurring ? {recurringFrequency, recurringEndCondition, numberOfOccurrences, recurringEndDate} : undefined;

        let valuationInput: ValuationInput = ValuationInputEnum.Cf;
        const updatedScenarios = scenarios.map(scenario => {
            const scenarioCode = scenario.scenario.code?.toLowerCase();
            if (!scenarioCode || selectedRowIndex === null) {
                return scenario;
            }

            const rowWithUpdate = this.rows.at(selectedRowIndex)?.get("type")?.value === "Amount" ? amountRow : navMultipleRow;
            if (!rowWithUpdate) {
                return scenario;
            }

            let newCashflows: AssetCashflow[] = [];
            if (amountRow && rowWithUpdate.get("type")?.value === amountRow.get("type")?.value) {
                const amount = DecimalFormatPipe.transformToMillionsNum(amountRow.value[scenarioCode]);
                const value = this.isInjectionCashflow() && amount ? amount * -1 : amount;
                valuationInput = ValuationInputEnum.Cf;

                newCashflows = value ? this.createRecurringCFs(cfDate, cfType, valuationInput, value, isRecurring, exitDate, recurringOptions) : [];

            } else if (navMultipleRow && rowWithUpdate.get("type")?.value === navMultipleRow.get("type")?.value) {
                const navMultiple = navMultipleRow.value[scenarioCode];
                const value = this.isInjectionCashflow() && navMultiple ? navMultiple * -1 : navMultiple;
                valuationInput = ValuationInputEnum.Multiple;

                newCashflows = navMultiple ? this.createRecurringCFs(cfDate, cfType, valuationInput, value, isRecurring, exitDate, recurringOptions) : [];
            }

            return {...scenario, cashflows: [...scenario.cashflows, ...newCashflows]};
        });

        this.hasDuplicates = false;
        updatedScenarios.forEach(scenario => this.checkForDuplicateCashflows(scenario));
        if (this.hasDuplicates) {
            return;
        }

        if (this.isValuationTypeDmv()) {
            this.store.dispatch(AssetValuationDetailActions.calcDMV({assetValuation, scenarios: updatedScenarios, save: true, valuationInput, withAlgorithm: false}));
            this.dialogRef.close();
        } else {
            this.store.dispatch(AssetValuationDetailActions.save({assetValuation, scenarios: updatedScenarios}));
            this.dialogRef.close();
        }
    }

    ngOnDestroy = () => this.subscriptions.forEach(sub => sub.unsubscribe());

    /*
     * when the end condition changes, update the validators for
     * the number of occurrences (required, max, min)
     * and recurring end date (required, date after fund report date)
     */
    private updateValidatorsBasedOnEndCondition(value: string): void {
        const numberOfOccurrences = this.cashflowForm.get("numberOfOccurrences");
        const recurringEndDate = this.cashflowForm.get("recurringEndDate");

        if (value === "occurrences") {
            numberOfOccurrences?.setValidators([Validators.required, Validators.min(1), Validators.max(99)]);
            recurringEndDate?.clearValidators();
        } else if (value === "date") {
            recurringEndDate?.setValidators([Validators.required, dateAfterValidator(DateUtil.toJsDate(this.fundReportDate?.date))]);
            numberOfOccurrences?.clearValidators();
        } else {
            numberOfOccurrences?.clearValidators();
            recurringEndDate?.clearValidators();
        }

        numberOfOccurrences?.updateValueAndValidity();
        recurringEndDate?.updateValueAndValidity();
    }

    private createRecurringCFs(
        cashflowDate: string,
        cashflowType: string,
        valuationInput: ValuationInput,
        value: number,
        isRecurring: boolean,
        exitDateAssumption?: DateTime,
        recurringOptions?: RecurringOptions): AssetCashflow[] {

        const createdCashflows: AssetCashflow[] = [];

        if (!isRecurring || !recurringOptions) {
            createdCashflows.push(this.createCashflow(cashflowDate, cashflowType, valuationInput, value));
            return createdCashflows;
        }

        let initialDate = DateUtil.fromIsoDate(cashflowDate);
        if (!initialDate || !recurringOptions.recurringFrequency) {
            return [];
        }

        const frequencyMapping: Record<string, { months: number }> = {
            "month": {months: 1},
            "quarter": {months: 3},
            "semester": {months: 6},
            "year": {months: 12}
        };
        const frequency = frequencyMapping[recurringOptions.recurringFrequency];

        const endCondition = (date: DateTime): boolean => {
            switch (recurringOptions.recurringEndCondition) {
                case "occurrences":
                    return recurringOptions.numberOfOccurrences ? createdCashflows.length < recurringOptions.numberOfOccurrences : false;
                case "date":
                    return recurringOptions.recurringEndDate ? date <= recurringOptions.recurringEndDate : false;
                case "exit":
                    return exitDateAssumption ? date <= exitDateAssumption : false;
                default:
                    return false;
            }
        };

        const originalDay = initialDate.day;

        while (endCondition(initialDate)) {

            createdCashflows.push(
                this.createCashflow(
                    DateUtil.toIsoDate(initialDate) ?? DateUtil.isoDate(),
                    cashflowType,
                    valuationInput,
                    value
                )
            );

            initialDate = DateUtil.addMonthsPreservingDay(initialDate, frequency.months, originalDay);
        }

        return createdCashflows;
    }

    private createCashflow(date: string, cashflowType: string, valuationInput: ValuationInput, value: number): AssetCashflow {
        return {
            ...EMPTY_ASSET_CASHFLOW,
            date: {...EMPTY_ASSET_CASHFLOW.date, date: date},
            cashflowType: {...EMPTY_ASSET_CASHFLOW.cashflowType, code: cashflowType},
            ...(valuationInput === ValuationInputEnum.Cf ? {amount: {...EMPTY_ASSET_CASHFLOW.amount, amount: value}} : {}),
            ...(valuationInput === ValuationInputEnum.Multiple ? {navMultiple: {...EMPTY_ASSET_CASHFLOW.navMultiple, factor: value}} : {})
        };
    }

    private checkForDuplicateCashflows(scenario: AssetValuationScenario) {
        const existingCashflows = new Map<string, Set<string>>();

        scenario.cashflows.some(cashflow => {
            if (!cashflow.date.date || !cashflow.cashflowType.code) {
                return false;
            }

            const typeSet = existingCashflows.get(cashflow.date.date) ?? new Set<string>();
            if (typeSet.has(cashflow.cashflowType.code)) {
                this.hasDuplicates = true;
                return true;
            }

            typeSet.add(cashflow.cashflowType.code);
            existingCashflows.set(cashflow.date.date, typeSet);
            return false;
        });
    }

    private amountOrNavFormValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const rows = control.get("rows") as any;
            if (!rows?.controls) {
                return null;
            }

            const amountGroup = rows.at(0);
            const navGroup = this.isValuationTypeDmv() ? rows.at(1) : null;

            const isAmountValid = this.validateGroup(amountGroup);
            const isNavValid = navGroup ? this.validateGroup(navGroup) : false;
            if ((isAmountValid && !isNavValid) || (!isAmountValid && isNavValid) || (isAmountValid && isNavValid)) {
                return null;
            }

            return {amountOrNavError: "Either all fields in Amount or NAV Multiple must be filled"};
        };
    }

    private validateGroup(group: AbstractControl): boolean {
        const low = group.get("low")?.value;
        const base = group.get("base")?.value;
        const high = group.get("high")?.value;

        return low !== null && base !== null && high !== null;
    }

    private duplicateCashflowsFormValidator() {
        const cashflowType = this.cashflowForm.controls["cashflowType"].value;
        const cashflowDate = this.cashflowForm.controls["cashflowDate"].value;

        if (this.scenarios && this.scenarios.length > 0 && cashflowDate && cashflowType) {
            this.hasDuplicates = false;
            this.scenarios.map(scenario => {
                scenario.cashflows.some(cashflow => {
                    if ((cashflow.cashflowType.code === "EXIT" && cashflow.date.date === DateUtil.toIsoDate(cashflowDate))
                        || (cashflow.date.date === DateUtil.toIsoDate(cashflowDate) && cashflow.cashflowType.code === cashflowType)
                    ) {
                        this.hasDuplicates = true;
                    }
                })
            });
        } else {
            this.hasDuplicates = false;
        }
    }
}
